import React, { useRef } from 'react'
import { DialogType } from '../../../../../../control/dialog/dialog'
import useLoading from '../../../../../../control/loading/useLoading'
import useTranslate from '../../../../../../i18n/useTranslate'
import useLookupService from '../../../../../../services/lookup/useLookupService'
import useWebService from '../../../../../../services/web/useWebService'
import authenStore from '../../../../../../stores/authen/authenStore'
import useManageProjectStore from '../../../../../../stores/manageProject/useManageProjectStore'
import checkPermission from '../../../../../../utilities/checkPermission'
import { OptType, TFormValue } from '../synthesisType'
import { SynthesisType } from './synthesis'
import * as REQServiceType from '../../../../../../services/web/types/requestWebServiceTypes'
import { TabSelectedType } from '../../../../../../stores/appFolderAndTab/appFolderAndTabStore'
import useFolderAndTab from '../../../useFolderAndTab'
import { useHistory } from 'react-router-dom'
import { RoutePath } from '../../../../../../router/routesConfig'
import _ from 'lodash'
import useAppFolderAndTabAction from '../../../../../../stores/appFolderAndTab/appFolderAndTabAction'
import { GetSynthesisProjectByIDResultType } from '../../../../../../services/web/types/responseWebServiceTypes'
import useEditSynthesis from '../../../../shareLogic/useEditSynthesis'
import submittedStep from '../../../../../../constants/submittedStep'

const useSynthesis = (props: SynthesisType) => {
    const { projectData, onSaveComplete } = props
    const [initialValues, setInitialValues] = React.useState<any>({})
    const [configDialog, setConfigDialog] =
        React.useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm' | 'onAfterClose'>>()
    const dialogRef = React.useRef<Record<string, Function>>()
    const checkPermissionHook = checkPermission()
    const isViewMode = checkPermissionHook.chkProjectStepViewMode(projectData)
    const isShowUpdateDate = checkPermissionHook.chkShowUpdateDate(projectData)
    const [consideration, setConsideration] = React.useState<OptType[]>([])
    const callLookup = useLookupService()
    const callWebService = useWebService()
    const translate = useTranslate()
    const manageProjectStore = useManageProjectStore()
    const loadingHook = useLoading()
    const profileStore = authenStore()
    const { profile } = profileStore
    const tabAndFolderHook = useFolderAndTab()
    const history = useHistory()
    const formValues = React.useRef<TFormValue>()
    const appFolderAndTabAction = useAppFolderAndTabAction()
    const formRef = useRef<any>()
    const editSynthesis = useEditSynthesis({ form: formRef.current })

    React.useEffect(() => {
        createInitialData()
    }, [projectData?.submittedID ?? null])

    React.useEffect(() => {
        if (projectData?.irb) {
            getConsideration(projectData?.irb)
        }
    }, [translate.languageStore.currentLanguage])

    const onRefDialog = (func: Record<string, Function>) => {
        //ห้าม set State หรือเรียก Action ในนี้
        dialogRef.current = func
    }

    const getConsideration = async (irb: number) => {
        let res = await callLookup.getApproveStatusByCloseProject(irb)
        const formatData = res?.result?.map((itm) => {
            return {
                label:
                    translate.languageStore.currentLanguage === translate.languageKey.th
                        ? itm.approveStatusName_th
                        : itm.approveStatusName_en,
                value: `${itm.approveStatusId}`,
            }
        }) as []
        setConsideration(formatData)
    }

    const getSynthesis = async () => {
        const submittedID = manageProjectStore?.projectData?.submittedID ?? null
        if (submittedID) {
            loadingHook.loadingOpen()
            const { result } = await callWebService.getSynthesisProjectByID(projectData.submittedID)

            loadingHook.loadingClose()
            return result
        }
    }

    /**
     * Default value form
     */
    const createInitialData = async () => {
        const result = await getSynthesis()

        if (result) {
            const initialData = {
                approveStatus:
                    result && result?.approveStatusID
                        ? {
                              label:
                                  translate.languageStore.currentLanguage === translate.languageKey.th
                                      ? result.approveStatusName_th
                                      : result.approveStatusName_en,
                              value: result?.approveStatusID.toString(),
                          }
                        : null,
                synthesis: conditionRenderSynthesisView(result),
            }
            setInitialValues(initialData)
        }
    }

    const conditionRenderSynthesisView = (result: GetSynthesisProjectByIDResultType) => {
        const isFirstTime = !result?.synthesisRemarkInfo && !result?.isDraft
        if (isFirstTime) {
            return null
        }

        if (result.synthesisRemarkInfo && result.synthesisRemarkInfo?.remark) {
            return result.synthesisRemarkInfo?.remark
        } else {
            return isViewMode ? '-' : null
        }
    }

    /**
     * process save
     */
    const handleSubmitForm = async (val: TFormValue) => {
        const parameter = getParameterForSave(val)
        if (val.submitType === 'save') {
            setConfigDialog({
                title: translate.get('ALERT.TITLE.NOTIFICATION'),
                content: translate.get('ALERT.CONTENT.NEED_TO_SAVE'),
                type: 'alert-confirm',
                onClickConfirm: () => {
                    handleConfirmSave(parameter)
                },
            })
            dialogRef.current?.openDialog()
        } else {
            //ถ้า save draft ไม่ต้องมี alert confirm
            handleConfirmSave(parameter)
        }
    }
    const getParameterForSave = (val: TFormValue) => {
        let paramForSave = null
        const submittedID = manageProjectStore?.projectData?.submittedID ?? null

        let isDraft = false
        if (val.submitType === 'draft') {
            isDraft = true
        }

        const synthesisProjectRequest: REQServiceType.SynthesisProjectType = {
            loginUserID: profile.userId,
            submittedID: submittedID,
            reviewerID: profile.userId,
            approveStatusID:
                val && val.approveStatus && val.approveStatus.value ? parseInt(val?.approveStatus.value) : null,
            synthesisInfo: [],
            synthesisRemark: val && val?.synthesis ? val?.synthesis : null,
            isDraft: isDraft,
        }
        paramForSave = synthesisProjectRequest

        return paramForSave
    }

    const handleConfirmSave = async (
        paramForSave: REQServiceType.SynthesisProjectType,
        tabIndex: number | null = null,
        newTabSelected: TabSelectedType | null = null,
        isFolder?: boolean,
    ) => {
        dialogRef.current?.closeDialog()

        const isDraft = paramForSave.isDraft ? paramForSave.isDraft : false
        const isAutoSave = !!(paramForSave as any)?.isAutoSave
        loadingHook.loadingOpen()
        const res = await callWebService.synthesisProject(paramForSave)

        if (!isFolder) {
            //if นี้จะเช็คว่าเป็นการติด dirty จากการกดเปลี่ยน folder ใช่หรือไม่?
            if (tabIndex && newTabSelected) {
                //if นี้จะเช็คว่าเป็นการติด dirty จากการกดเปลี่ยน Tab ใช่หรือไม่?
                tabAndFolderHook.setTab(tabIndex, newTabSelected)
            } else {
                //หากเข้า else หมายความว่าเป็นการบันทึกจากกดปุ่มบันทึกไม่ใช่จากการติด dirty
                if (onSaveComplete && !isAutoSave) {
                    if (res && res.status) {
                        onSaveComplete(res.status, isDraft)
                    }
                }
            }
        }
        loadingHook.loadingClose()
    }

    const handleAutoSave = async (values: TFormValue) => {
        try {
            const isPassedSynthesis = (projectData?.stepID ?? 0) > submittedStep.synthesis
            if (isPassedSynthesis) return

            const paramForSave = getParameterForSave({ ...values, submitType: 'draft' })
            // check autosave
            const hasSynthesisContent = (paramForSave.synthesisInfo ?? []).some((item) =>
                Boolean(item.contentText?.trim() || item.contentHtml?.trim()),
            )
            if (!hasSynthesisContent && !paramForSave.synthesisRemark?.trim()) return
            ;(paramForSave as any).isAutoSave = true
            //silent = true กัน dialog เด้งตอน autosave พลาด ผู้ใช้ไม่ได้กดบันทึกเอง
            await callWebService.synthesisProject(paramForSave, true)
        } catch (error) {
            console.error(error)
        }
    }

    const handleClickBackToSearch = (dirty: boolean) => {
        if (dirty) {
            setConfigDialog({
                title: translate.get('ALERT.TITLE.WARNING'),
                content: translate.get('ALERT.CONTENT.NEED_TO_CANCEL'),
                type: 'alert-confirm',
                onClickConfirm: () => {
                    history.push(RoutePath.manageProject)
                },
            })
            dialogRef.current?.openDialog()
        } else {
            history.push(RoutePath.manageProject)
        }
    }

    /**
     * Dirty
     */

    const handleDirtyChange = (props: any) => {
        formValues.current = { ...props.values }
        let isDirty: boolean = false
        //** จัดformat initValue และ Form Value เพื่อเตรียมตัวนำไปใช้เปรียบเทียบค่า  **//
        let newFormValue = { ...props.values }
        let initValue = { ...initialValues }

        //**********************************************************//
        const isEqual = _.isEqual(initValue, newFormValue) //ข้อมูลเท่ากัน

        if (!isViewMode) {
            if (isEqual) {
                isDirty = false
            } else {
                isDirty = true
            }
        }

        const paramsDirty = {
            tabDirty: isDirty,
            callbackDirty: callbackDirtyProcess,
        }
        appFolderAndTabAction.setDirty(paramsDirty)
    }

    const callbackDirtyProcess = (
        tabIndex: number | null = null,
        newTabSelected: TabSelectedType | null = null,
        isFolder?: boolean,
    ) => {
        const parameter = getParameterForSave(formValues.current)
        if (parameter) {
            parameter.isDraft = true
            handleConfirmSave(parameter, tabIndex, newTabSelected, isFolder)
        }
    }

    const updateAfterSubmitted = async (names: string[]) => {
        formRef.current?.change('submitType', 'save')
        const formProps = formRef.current?.getState()
        const parameter = {
            ...getParameterForSave(formValues.current),
            approveStatusID: formProps?.values?.approveStatus?.value ?? null,
            synthesisRemark: formProps?.values?.synthesis ?? null,
        }
        let isError = false
        const errKeys = Object.keys(formProps.errors)
        names.forEach((name) => {
            const findError = errKeys.includes(name)
            if (findError) {
                isError = true
            }
        })
        loadingHook.loadingOpen()
        return new Promise((resolve, reject) => {
            if (isError) {
                Promise.all([callWebService.synthesisProject(parameter)])
                    .then(async () => {
                        await loadTabAndFolder()
                        resolve('')
                    })
                    .catch(() => {
                        reject('')
                    })
            } else {
                formRef.current?.submit()
                reject('')
            }
        }).finally(() => {
            loadingHook.loadingClose()
        })
    }

    const loadTabAndFolder = async () => {
        if (manageProjectStore?.projectData) {
            await tabAndFolderHook.reloadProjectData(
                manageProjectStore.projectData.projectID,
                manageProjectStore.projectData.submittedID,
            )
        }
    }

    return {
        configDialog,
        isViewMode,
        consideration,
        initialValues,
        isShowUpdateDate,
        useAccordionMode: editSynthesis.useAccordionMode,
        formRef,
        updateAfterSubmitted,
        onRefDialog,
        onAutoSave: handleAutoSave,
        onSubmitForm: handleSubmitForm,
        onClickBackToSearch: handleClickBackToSearch,
        onDirtyChange: handleDirtyChange,
    }
}
export default useSynthesis
